import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { LoadingContainer } from "go-core/components/Loading";
import { FormDirty } from "go-form/components/FormDirty";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { ClientDetailsImportContext, ClientGrantApi } from "../../../services/types";

interface Props {
	show: boolean;
	clientId: string;
	grantId: number | undefined;
	onHide: () => void;
	requests: ClientDetailsImportContext;
	handleRefreshList(): Promise<void>;
	organizationName: string | undefined;
}

const ClientGrantFormModal = ({
	clientId,
	onHide,
	show,
	requests,
	grantId,
	handleRefreshList,
	organizationName,
}: Props) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const [submitLoading, setSubmitLoading] = useState(false);
	const [clientGrant, setClientGrant] = useState<ClientGrantApi | undefined>();
	const { addFlash, addSuccessFlash } = useFlash();
	const form = useForm<ClientGrantApi>({
		defaultValues: {
			client_id: clientId,
			id: grantId,
		},
	});
	const {
		control,
		formState,
		formState: { errors },
		setError,
		handleSubmit,
		setValue,
	} = form;

	useEffect(() => {
		if (grantId) {
			fetchClientGrant();
		}
	}, []);

	const fetchClientGrant = async () => {
		setLoading(true);
		try {
			const res = await requests.getClientGrant(clientId, grantId!);
			setClientGrant(res);
			setValue("organization_id", res.organization_id);
			setValue(
				"granted_scopes",
				typeof res.granted_scopes === "string" ? res.granted_scopes.split(",") : res.granted_scopes
			);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	const onSubmit = handleSubmit(async (data) => {
		setSubmitLoading(true);
		try {
			const newData = {
				...data,
				granted_scopes:
					typeof data.granted_scopes !== "string" ? data.granted_scopes.join(",") : data.granted_scopes,
			};
			await (grantId
				? requests.updateClientGrant(clientId, newData)
				: requests.createClientGrant(clientId, newData));
			addSuccessFlash(t("lib:common.flash.saved"));
			await handleRefreshList();
			onHide();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setSubmitLoading(false);
	});

	const getScopes = async () => {
		const scopes = await requests.getClientScopes();
		return scopes.map((scope) => ({ value: scope, label: scope }));
	};

	const getDefaultScopesValue = () => {
		if (!clientGrant?.granted_scopes) return "";

		if (typeof clientGrant.granted_scopes === "string") {
			return clientGrant.granted_scopes.split(",").map((scope) => ({
				value: scope,
				label: scope,
			}));
		}
		return clientGrant.granted_scopes;
	};

	return (
		<Modal show={show} onHide={onHide}>
			<FormDirty formState={formState} onSubmit={onSubmit}>
				<Modal.Header closeButton>
					<Modal.Title>
						{grantId
							? t("lib:go_component.client_details.action.edit_client_grant.title", {
									organizationName: organizationName ?? "",
							  })
							: t("lib:go_component.client_details.action.add_client_grant.title")}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{loading ? (
						<LoadingContainer />
					) : (
						<div className="container px-0">
							<div className="row">
								<div className="col-md-6">
									<FormSelectGroup
										label="Scopes"
										name="granted_scopes"
										control={control}
										loadOptions={getScopes}
										errors={errors}
										isMulti
										defaultValue={getDefaultScopesValue()}
										data-testid="granted_scopes"
									/>
								</div>
								<div className="col-md-6">
									<FormNumberInput
										disabled={!!grantId}
										name="organization_id"
										errors={errors}
										control={control}
										label={t("lib:go_component.client_details.field.organization_id.title")}
										suffix=""
									/>
								</div>
							</div>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={submitLoading} onClick={onSubmit}>
						{t("lib:common.action.save")}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default ClientGrantFormModal;
